import {useEffect, useRef, useState} from 'react';
import AxisDropdown from './AxisDropdown';
import SettingModal from './SettingModal';
import TypeDropdown from './TypeDropdown';
import {ReactComponent as ErrorIcon} from '../../assets/chart/error.svg';
import {ReactComponent as SettingIcon} from '../../assets/chart/setting.svg';
import {ReactComponent as SettingActiveIcon} from '../../assets/chart/setting-active.svg';
import {ReactComponent as SortToggleIcon} from '../../assets/chart/sort-toggle.svg';
import styles from './styles/box.module.css';

/* 차트 관련 */
import {Bar, Line, Pie} from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Tooltip, Legend} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Tooltip, Legend, ChartDataLabels);

export default function ChartBox({ data, dataType }) {
    const formatKey = (key) => {
        return key.split('.').pop();
    };

    const xAxisList = Object.keys(dataType)
        .filter((key) => dataType[key] === 'STRING')
        .map((key) => ({ originalKey: key, displayKey: formatKey(key) }));

    const yAxisList = Object.keys(dataType)
        .filter((key) => dataType[key] === 'FLOAT' || dataType[key] === 'INTEGER')
        .map((key) => ({ originalKey: key, displayKey: formatKey(key) }));

    // 선택된 displayKey에 해당하는 originalKey 찾는 함수
    const findOriginalKey = (axisList, displayKey) => {
        const item = axisList.find((axis) => axis.displayKey === displayKey);
        return item ? item.originalKey : null;
    };

    const settingModalRef = useRef(null);

    const [chartType, setChartType] = useState('bar');

    const [xAxis, setXAxis] = useState(xAxisList[0]?.displayKey || '');
    const [yAxis, setYAxis] = useState(yAxisList[0]?.displayKey || '');

    const [settings, setSettings] = useState([
        { variable: yAxis, chartType: chartType, standard: yAxis },
        { variable: '', chartType: '', standard: yAxis },
        { variable: '', chartType: '', standard: yAxis },
        { variable: '', chartType: '', standard: yAxis },
        { variable: '', chartType: '', standard: yAxis }
    ]);

    const [openModal, setOpenModal] = useState(false);

    const [isMixedChart, setIsMixedChart] = useState(false);

    const [chartError, setChartError] = useState(false);

    const [sortedText, setSortedText] = useState('오름차순');
    const [sortedData, setSortedData] = useState(data);
    const sortData = (order) => {
        const sorted = [...data].sort((a, b) => {
            if (order === 'asc') {
                setSortedText('내림차순');
                return a[findOriginalKey(xAxisList, xAxis)] > b[findOriginalKey(xAxisList, xAxis)] ? 1 : -1;
            } else {
                setSortedText('오름차순');
                return a[findOriginalKey(xAxisList, xAxis)] < b[findOriginalKey(xAxisList, xAxis)] ? 1 : -1;
            }
        });
        setSortedData(sorted);
    };

    const updateSetting = (index, newSetting) => {
        setSettings((prevSettings) => {
            const updatedSettings = [...prevSettings];
            updatedSettings[index] = newSetting;
            return updatedSettings;
        });
    };

    const filteredYAxisList = () => {
        const selectedVariables = settings
            .filter((setting) => setting.variable)
            .map(setting => setting.variable);

        return yAxisList.filter(item => !selectedVariables.includes(item.displayKey));
    };

    const changeMixedMode = () => {
        setOpenModal(true);
        setIsMixedChart(true);
    };

    const getMinMax = (data, yAxisId) => {
        const values = data.map(item => item[yAxisId]).filter(value => value !== null && value !== undefined);
        const min = Math.min(...values);
        const max = Math.max(...values);
        return { min, max };
    };

    useEffect(() => {
        setSettings([
            { variable: yAxis, chartType: chartType, standard: yAxis },
            { variable: '', chartType: '', standard: yAxis },
            { variable: '', chartType: '', standard: yAxis },
            { variable: '', chartType: '', standard: yAxis },
            { variable: '', chartType: '', standard: yAxis }
        ]);
    }, [yAxis, chartType]);

    useEffect(() => {
        if (openModal && settingModalRef.current) {
            settingModalRef.current.scrollIntoView({ behavior: 'smooth', inline: 'start' });
        }
    }, [openModal]);

    useEffect(() => {
        sortData('asc');
    }, [data, xAxis, openModal]);

    const colorPalette = [ '#A79CFF', '#825DFC', '#F1547E', '#E334EC', '#FCD039' ];
    const colorOpacityPalette = [ 'rgba(167, 156, 255, 0.5)', 'rgba(130, 93, 252, 0.5)', 'rgba(241, 84, 126, 0.5)', 'rgba(227, 52, 236, 0.5)', 'rgba(252, 208, 57, 0.5)' ];

    const generateChartData = () => {
        const labels = sortedData.map((item) => item[findOriginalKey(xAxisList, xAxis)]);
        const yAxisData = sortedData.map((item) => item[findOriginalKey(yAxisList, yAxis)]);

        return {
            labels,
            datasets: [
                {
                    label: yAxis,
                    data: yAxisData,
                    backgroundColor: '#825DFC',
                    borderWidth: 1,
                    borderRadius: 5,
                    borderColor: '#825DFC',
                    pointRadius: 1,
                }
            ]
        };
    };

    const generatePieChartData = () => {
        const labels = sortedData.map((item) => item[findOriginalKey(xAxisList, xAxis)].toString());
        const yAxisData = sortedData.map((item) => item[findOriginalKey(yAxisList, yAxis)]);
    
        // 중복된 레이블이 있으면 에러 처리
        const uniqueLabelsSet = new Set(labels);
        if (uniqueLabelsSet.size !== labels.length) {
            if (!chartError) {
                setChartError(true);
            }
            return null;
        }

        if (!yAxisData || yAxisData.length < 2 || yAxisData.length > 50) { /* 2개 이상 50개 이하만 원 그래프 출력 가능 */
            setChartError(true);
            return null;
        }

        if (chartError) { // 에러 상태를 해제할 때 조건을 추가하여 무한 루프 방지
            setChartError(false);
        }

        let displayedLabels = labels;
        let displayedData = yAxisData;

        if (yAxisData.length > 29) { /* 30 순서부터는 기타로 묶음 */
            const first29Labels = labels.slice(0, 29);
            const first29Data = yAxisData.slice(0, 29);
            const otherDataSum = yAxisData.slice(29).reduce((acc, val) => acc + val, 0);

            displayedLabels = [...first29Labels, '기타'];
            displayedData = [...first29Data, otherDataSum];
        }

        return {
            labels: displayedLabels,
            datasets: [
                {
                    data: displayedData,
                    backgroundColor: ['#FF6384', '#4F93FF', '#FFCE56', '#54A971', '#D095E9', '#BD8088', '#FFB487', '#00ACB2'],
                    hoverBackgroundColor: ['#FF6384', '#4F93FF', '#FFCE56', '#54A971', '#D095E9', '#BD8088', '#FFB487', '#00ACB2']
                }
            ]
        };
    };

    const generateMixedChartData = () => {
        const labels = sortedData.map((item) => item[findOriginalKey(xAxisList, xAxis)]);

        // 각 설정(setting)에 맞춰 데이터셋 생성
        const datasets = settings
            .filter(setting => setting.variable && setting.chartType)
            .map((setting, index) => {
                const yAxisData = sortedData.map((item) => item[findOriginalKey(yAxisList, setting.variable)]);

                return {
                    type: setting.chartType,
                    label: findOriginalKey(yAxisList, setting.variable),
                    data: yAxisData,
                    backgroundColor: setting.chartType === 'bar'
                        ? colorOpacityPalette[index]
                        : colorPalette[index],
                    borderColor: colorOpacityPalette[index],
                    borderWidth: 1,
                    borderRadius: 5,
                    pointBackgroundColor: colorPalette[index],
                    pointBorderColor: colorPalette[index],
                    pointRadius: 1,
                    yAxisID: findOriginalKey(yAxisList, setting.standard) // Y축을 standard에 따라 다르게 설정
                };
            });

        return {
            labels,
            datasets
        };
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            datalabels: {
                display: false
            },
            legend: {
                display: false
            },
            title: {
                display: false
            }
        },
        scales: {
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    autoSkip: true, // 자동 생략 활성화
                }
            },
            y: {
                grid: {
                    display: true
                }
            }
        }
    };

    const pieChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'right',
                labels: {
                    usePointStyle: false
                }
            },
            tooltip: {
                callbacks: {
                    label: function (tooltipItem) {
                        const total = tooltipItem.dataset.data.reduce((acc, val) => acc + val, 0);
                        const currentValue = tooltipItem.raw;
                        const percentage = ((currentValue / total) * 100).toFixed(2);
                        return `${tooltipItem.label}: ${percentage}%`;
                    }
                }
            },
            datalabels: {
                formatter: (value, context) => {
                    const dataset = context.chart.data.datasets[0];
                    const total = dataset.data.reduce((acc, val) => acc + val, 0);
                    const percentage = ((value / total) * 100).toFixed(2);
                    return `${percentage}%`;
                },
                color: '#fff',
                anchor: 'center',
                align: 'center'
            }
        }
    };

    const mixedChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            datalabels: {
                display: false
            },
            legend: {
                display: false
            },
            title: {
                display: false
            }
        },
        scales: {
            x: {
                grid: {
                    display: false
                }
            },
            ...settings.reduce((acc, setting, index) => {
                const yAxisID = findOriginalKey(yAxisList, setting.standard);
                const { min, max } = getMinMax(sortedData, yAxisID);

                // yAxisID가 이미 설정된 경우 중복 방지
                if (!acc[yAxisID]) {
                    // 각 yAxisID에 대해 축을 동적으로 설정
                    acc[yAxisID] = {
                        type: 'linear',
                        display: true,
                        position: index % 2 === 0 ? 'left' : 'right', // 짝수 인덱스는 왼쪽, 홀수 인덱스는 오른쪽
                        ticks: {
                            color: colorPalette[index], // y축 색상 설정
                            suggestedMin: min,
                            suggestedMax: max
                        },
                        grid: {
                            drawOnChartArea: true // 첫 번째 Y축 그리드를 표시하고, 필요시 조정
                        }
                    };
                }
                return acc;
            }, {})
        }
    };

    return (
        <div className={styles.wrapper}>
            {data.length !== 0 && xAxisList.length !== 0 && yAxisList.length !== 0
                ? <>
                    <div className={styles.containerContent}>
                        <TypeDropdown chartType={chartType} setChartType={setChartType} setIsMixedChart={setIsMixedChart} />
                        <div className={styles.containerDetail}>
                            <button className={styles.btnSort} onClick={sortedText === '오름차순' ? () => sortData('asc') : () => sortData('desc')}>
                                {sortedText}
                                <SortToggleIcon/>
                            </button>
                            <div className={styles.boxAxis}>
                                <div className={styles.axisInfo}>X</div>
                                <AxisDropdown axisList={xAxisList} axis={xAxis} setAxis={setXAxis}/>
                            </div>
                            <div className={styles.boxAxis}>
                                <div className={styles.axisInfo}>Y</div>
                                <AxisDropdown axisList={yAxisList} axis={yAxis} setAxis={setYAxis} settings={settings}/>
                            </div>
                            {chartType === 'pie'
                                ? null
                                : <div>
                                    {openModal
                                        ? <SettingActiveIcon className={styles.iconSetting}/>
                                        : <SettingIcon className={styles.iconSetting} onClick={changeMixedMode} />
                                    }
                                </div>
                            }
                            {openModal && <div ref={settingModalRef} className={styles.boxRef}>
                                <SettingModal yAxisList={yAxisList}
                                              filteredYAxisList={filteredYAxisList}
                                              settings={settings}
                                              updateSetting={updateSetting}
                                              closeModal={() => setOpenModal(false)}
                                />
                            </div>
                            }
                        </div>
                    </div>
                    <div className={styles.containerChart}>
                        {isMixedChart
                            ? <Bar data={generateMixedChartData()} options={mixedChartOptions}/>
                            : <>
                                {chartType === 'bar'
                                    ? (!xAxis || !yAxis)
                                        ? <div className={styles.boxError}>
                                            <ErrorIcon/>
                                            <p>그래프 제공 불가</p>
                                            <p>그래프를 제공할 수 있는 데이터 형식이 아닙니다.</p>
                                        </div>
                                        : <Bar data={generateChartData()} options={chartOptions}/>
                                    : null
                                }
                                {chartType === 'line'
                                    ? (!xAxis || !yAxis)
                                        ? <div className={styles.boxError}>
                                            <ErrorIcon/>
                                            <p>그래프 제공 불가</p>
                                            <p>그래프를 제공할 수 있는 데이터 형식이 아닙니다.</p>
                                        </div>
                                        : <Line data={generateChartData()} options={chartOptions}/>
                                    : null
                                }
                                {chartType === 'pie'
                                    ? chartError
                                        ? <div className={styles.boxError}>
                                            <ErrorIcon/>
                                            <p>그래프 제공 불가</p>
                                            <p>그래프를 제공할 수 있는 데이터 형식이 아닙니다.</p>
                                        </div>
                                        : <Pie data={generatePieChartData()} options={pieChartOptions}/>
                                    : null
                                }
                            </>
                        }
                    </div>
                </>
                : <div className={styles.boxError}>
                    <ErrorIcon/>
                    <p>그래프 제공 불가</p>
                    <p>그래프를 제공할 수 있는 데이터 형식이 아닙니다.</p>
                </div>
            }
        </div>
    );
}
